// @flow

import React, { PureComponent } from 'react';
import type { Element } from 'react';
import PropTypes from 'prop-types';
import Swipeable from 'react-swipeable';
import * as R from 'ramda';

// Components
import Icon from 'Common/Icon';
import TagHtml from 'Common/TagHtml';

// Styles
import styles from 'components/Carousel.scss';

// Helpers
import classNames from 'classnames';
import PaginationCarousel from '../PaginationCarousel';
import { gtmEventPush } from 'utils/gtm/events';

/**
 * Carousel React component.
 * @returns {ReactElement} the carousel component
 */
class Carousel extends PureComponent<*, *> {
  itemsLength: number;

  /**
   * Props implementation.
   * @param {object} props - component props
   */
  constructor(props: Object) {
    super(props);
    this.itemsLength = this.props.children ? this.props.children.length : 0;
    this.state = {
      activeItemIndex: this.props.activeItemIndex || 0
    };
  }

  /* eslint-disable camelcase */
  /**
   * MapStateToProps implementation.
   * @param {object} nextProps object
   */
  UNSAFE_componentWillReceiveProps(nextProps: Object) {
    if (this.props.children !== nextProps.children) {
      this.itemsLength = nextProps.children ? nextProps.children.length : 0;
      this.setState({
        activeItemIndex: nextProps.activeItemIndex || 0
      });
    }
  }

  /**
   *  Props implementation.
   */
  static propTypes = {
    children: PropTypes.array,
    pictures: PropTypes.array,
    activeItemIndex: PropTypes.number,
    itemsToDisplay: PropTypes.number,
    extendStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    showPagination: PropTypes.bool,
    showSlider: PropTypes.bool,
    showCaption: PropTypes.bool,
    paginationType: PropTypes.string,
    handlePrevious: PropTypes.func,
    handleNext: PropTypes.func,
    arrowLeft: PropTypes.string,
    arrowRight: PropTypes.string,
    activeThumb: PropTypes.number,
    carouselType: PropTypes.string,
    openModal: PropTypes.func,
    handleOnClick: PropTypes.func,
    viewport: PropTypes.string,
    gtmSectionName: PropTypes.string.isRequired
  };

  /**
   * Implements defaultProps().
   */
  static defaultProps: Object = {
    showSlider: true,
    showPagination: false,
    paginationType: 'paginationDots',
    visibleItems: 1,
    itemsToDisplay: 1,
    arrowRight: 'arrow-e',
    arrowLeft: 'arrow-w',
    carouselType: 'default'
  };

  /**
   * navigates to new slide
   * @param {string} direction - previous or next
   * @return {void}
   */
  handleNavigate: (direction: string) => void = (direction: string) => {
    const { activeItemIndex } = this.state;
    const { itemsToDisplay } = this.props;
    let newIndex = 0;
    let groupItem = Math.ceil(this.itemsLength / itemsToDisplay);
    if (direction === 'previous') {
      newIndex = activeItemIndex - 1 === -1 ? 0 : activeItemIndex - 1;

      // If its first page than move slide last page
      newIndex = activeItemIndex === 0 ? groupItem - 1 : newIndex;
    } else {
      newIndex =
        activeItemIndex + 1 === this.itemsLength
          ? this.itemsLength
          : activeItemIndex + 1;
      // If its last page than move slide first page
      // console.log('+++?', ); //
      newIndex = activeItemIndex === groupItem - 1 ? 0 : newIndex;
    }

    let visibleItems = this.setVisible(newIndex);
    this.setState({ activeItemIndex: newIndex, visibleItems });
  };

  /**
   * Determines which elements are going to be displayed.
   * @param {number} activeItemIndex - current active
   * @return {array} allElements
   */
  setVisible: (activeItemIndex: number) => any = (activeItemIndex: number) => {
    const { itemsToDisplay } = this.props;
    let allElements = R.range(0, this.itemsLength);
    let sliceStart = activeItemIndex * itemsToDisplay;
    let sliceEnd = activeItemIndex * itemsToDisplay + itemsToDisplay;
    return allElements.slice(sliceStart, sliceEnd);
  };

  /**
   * updates active item
   * @param {string} index - new index
   * @return {void}
   */
  goToSlide: (index: number) => any = (index: number) => {
    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        itemLabel: 'Pagination dot'
      }
    });
    let visibleItems = this.setVisible(index);
    this.setState({ activeItemIndex: index, visibleItems });
  };

  /**
   * Handles element click.
   * @param {string} index - new index
   * @return {void}
   */
  handleOnClick: (index: number) => any = (index: number) => {
    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        itemLabel: 'Pagination thumbnail'
      }
    });
    this.props.handleOnClick
      ? this.props.handleOnClick(index)
      : this.goToSlide(index);
  };
  /**
   * handlePrevious handles prev button click.
   * @return {void}
   */
  handlePrevious: () => void = () => {
    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        itemLabel: this.props.showSlider ? `Left arrow` : 'Swipe Left'
      }
    });

    this.props.handlePrevious
      ? this.props.handlePrevious('previous')
      : this.handleNavigate('previous');
  };

  /**
   * handleNext handles next button click.
   * @return {void}
   */
  handleNext: () => void = () => {
    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        itemLabel: this.props.showSlider ? `Right arrow` : 'Swipe Right'
      }
    });

    this.props.handleNext
      ? this.props.handleNext('next')
      : this.handleNavigate('next');
  };

  /**
   * Render
   * @return {ReactElement} markup
   */
  render(): Element<*> | null {
    const {
      children,
      extendStyle,
      itemsToDisplay,
      showSlider,
      arrowLeft,
      arrowRight,
      showCaption,
      carouselType,
      activeThumb,
      viewport,
      showPagination
    } = this.props;

    const {
      classCarousel,
      classCarouselItem,
      classCarouselItems,
      classCarouselActiveItem,
      classCarouselNextActiveItem,
      classCarouselNavigationPrev,
      classCarouselNavigationNext,
      classCarouselNavigationSmall,
      classCarouselInactive,
      classCarouselItemLength,
      classCarouselThumb,
      classCarouselCurrentActive,
      classLightSlider,
      classFullSlider,
      classCarouselCursorPointer
    } = styles;
    if (!children) {
      return null;
    }

    const isSmallDevice = viewport === 'xs' || viewport === 'sm' || false;

    let groupItem = Math.ceil(this.itemsLength / itemsToDisplay);
    const activeItemIndex =
      this.state.activeItemIndex < 0 && this.props.activeItemIndex >= 0
        ? this.props.activeItemIndex
        : this.state.activeItemIndex;

    const visibleItems =
      this.state.activeItemIndex < 0 && this.props.activeItemIndex >= 0
        ? this.setVisible(this.props.activeItemIndex)
        : this.setVisible(this.state.activeItemIndex);

    let flexBasis = {
      flex: 1 / itemsToDisplay
    };

    return (
      <Swipeable
        onSwipedLeft={
          activeItemIndex !== groupItem - 1 ? this.handleNext : null
        }
        onSwipedRight={activeItemIndex > 0 ? this.handlePrevious : null}
      >
        <div
          className={classNames([
            extendStyle,
            classCarousel,
            carouselType !== 'paginationThumb' || classCarouselThumb
          ])}
        >
          {showSlider && (
            <div
              className={classNames(
                classCarouselNavigationSmall,
                classCarouselNavigationPrev,
                carouselType === 'lightSlider'
                  ? classLightSlider
                  : classFullSlider,
                activeItemIndex > 0 || classCarouselInactive
              )}
            >
              <button onClick={this.handlePrevious}>
                <Icon iconName={arrowLeft} />
              </button>
            </div>
          )}

          <div
            className={classNames([
              classCarouselItems,
              this.itemsLength <= 1 || classCarouselCursorPointer
            ])}
          >
            {React.Children.map(children, (child, index) => (
              <div
                style={visibleItems.indexOf(index) > -1 ? flexBasis : null}
                className={classNames([
                  classCarouselItem,
                  visibleItems.indexOf(index) > -1
                    ? classCarouselActiveItem
                    : null,
                  visibleItems.indexOf(index - 1) > -1 && isSmallDevice
                    ? classCarouselNextActiveItem
                    : null,
                  index !== activeThumb || classCarouselCurrentActive
                ])}
                onClick={
                  carouselType === 'paginationThumb'
                    ? () => this.handleOnClick(index)
                    : this.props.openModal
                }
                key={index}
              >
                {child}
              </div>
            ))}
          </div>
          {showCaption && (
            <div
              className={classCarouselItemLength}
              onClick={this.props.openModal}
            >
              <TagHtml content={activeItemIndex + 1 + '/' + this.itemsLength} />

              {this.itemsLength > 1 && !isSmallDevice && (
                <Icon
                  iconName={'open-2'}
                  size={'medium'}
                  className={styles.icon}
                />
              )}
            </div>
          )}
          {showSlider && (
            <div
              className={classNames(
                classCarouselNavigationSmall,
                classCarouselNavigationNext,
                carouselType === 'lightSlider'
                  ? classLightSlider
                  : classFullSlider,
                activeItemIndex !== groupItem - 1 || classCarouselInactive
              )}
            >
              <button onClick={this.handleNext}>
                <Icon iconName={arrowRight} />
              </button>
            </div>
          )}
          {showPagination && (
            <PaginationCarousel
              numItems={groupItem}
              handleNext={this.handleNext}
              handlePrevious={this.handlePrevious}
              handleItem={this.goToSlide}
              activeItemIndex={activeItemIndex}
              paginationType={'dots'}
            />
          )}
        </div>
      </Swipeable>
    );
  }
}

export default Carousel;



// WEBPACK FOOTER //
// ./src/Common/Carousel/index.js